/**
 * @prettier
 */

import React, { useState, useEffect } from 'react';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import cx from 'classnames';
import { connect } from 'react-redux';
import { openModal } from 'cards/actions/modalActions';
import {
  // FILTER_KEYS,
  MORE_FILTER_LIST_MOBILE,
} from 'cards/components/CardListing/CardListingRedesign/CardListingFilter/constants';
import type { AppDispatch } from 'reducers/index';
import type { ModalOptions } from 'components/common/types';
import type { CardSettings } from 'cards/constants/CardTypes';
import ProvisionalSideDrawer from '../../ProvisionalComponents/ProvisionalSideDrawer';
import {
  MagnetMenu,
  CustomFoilMenu,
  LetterpressMenu,
  ReceptionMenu,
  FeaturedArtistMenu,
  MobileOrientationMenu,
  MobileCustomPhotoMenu,
} from '../Menus/Menus';

import type { ActiveFilters } from '../FilterTypes';
import { existenceCheck } from '../../utils';

import styles from './styles.module.less';

const MoreFiltersMobile = ({
  cardCatalogBusy,
  activeFilters,
  updateMultipleFilters,
  onClose,
  displayMoreFilters,
  hasThirdPartyDesigns,
  hasMultipleOrientations,
  hasLetterpress,
  hasFoil,
  hasNoCustomPhoto,
  hasCustomPhoto,
  isVowRenewal,
  hasMagnet,
  showModal,
}: MoreFiltersMobileProps): JSX.Element => {
  const [localFilters, setLocalFilters] = useState({
    has_magnet: activeFilters.has_magnet,
    has_custom_photo: activeFilters.has_custom_photo,
    has_no_custom_photo: activeFilters.has_no_custom_photo,
    has_foil: activeFilters.has_foil,
    is_letterpress: activeFilters.is_letterpress,
    is_vow_renewal: activeFilters.is_vow_renewal,
    is_landscape: activeFilters.is_landscape,
    is_portrait: activeFilters.is_portrait,
    featured_artist: activeFilters.featured_artist,
  } as MoreFiltersMobileSubset);

  const noLocalFilterSelected =
    MORE_FILTER_LIST_MOBILE.filter(filterKey => {
      const keyedFilter = filterKey as keyof MoreFiltersMobileSubset;
      const currentValue = localFilters[keyedFilter] === undefined ? [] : localFilters[keyedFilter];
      return existenceCheck(currentValue);
    }).length < 1;

  useEffect(() => {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ has_magnet: boolean; has_custo...
    setLocalFilters({
      has_magnet: activeFilters.has_magnet,
      has_custom_photo: activeFilters.has_custom_photo,
      has_no_custom_photo: activeFilters.has_no_custom_photo,
      has_foil: activeFilters.has_foil,
      is_letterpress: activeFilters.is_letterpress,
      is_vow_renewal: activeFilters.is_vow_renewal,
      is_landscape: activeFilters.is_landscape,
      is_portrait: activeFilters.is_portrait,
      featured_artist: activeFilters.featured_artist,
    });
  }, [activeFilters, displayMoreFilters]);

  function updateLocalFilter(key: keyof MoreFiltersMobileSubset, val: boolean) {
    setLocalFilters({
      ...localFilters,
      [key]: val,
    });
  }

  function applyFilter() {
    // If no change
    if (
      localFilters.has_magnet === activeFilters.has_magnet &&
      localFilters.has_custom_photo === activeFilters.has_custom_photo &&
      localFilters.has_no_custom_photo === activeFilters.has_no_custom_photo &&
      localFilters.has_foil === activeFilters.has_foil &&
      localFilters.is_letterpress === activeFilters.is_letterpress &&
      localFilters.is_vow_renewal === activeFilters.is_vow_renewal &&
      localFilters.is_landscape === activeFilters.is_landscape &&
      localFilters.is_portrait === activeFilters.is_portrait &&
      localFilters.featured_artist === activeFilters.featured_artist
    ) {
      onClose();
    } else {
      updateMultipleFilters({
        has_magnet: localFilters.has_magnet,
        has_custom_photo: localFilters.has_custom_photo,
        has_no_custom_photo: localFilters.has_no_custom_photo,
        has_foil: localFilters.has_foil,
        is_letterpress: localFilters.is_letterpress,
        is_vow_renewal: localFilters.is_vow_renewal,
        is_landscape: localFilters.is_landscape,
        is_portrait: localFilters.is_portrait,
        featured_artist: localFilters.featured_artist,
      });
      onClose();
    }
  }

  function clearLocalFilters() {
    setLocalFilters({
      ...localFilters,
      has_magnet: false,
      has_custom_photo: false,
      has_no_custom_photo: false,
      has_foil: false,
      is_letterpress: false,
      is_vow_renewal: false,
      is_landscape: false,
      is_portrait: false,
      featured_artist: false,
    });
  }

  return (
    <ProvisionalSideDrawer
      displayDrawer={displayMoreFilters}
      onClose={onClose}
      header="More"
      clearBtn={
        <ButtonV2
          className={cx(styles.clearBtn, styles.clearBtnMobile, {
            [styles.clearBtnDisabled]: noLocalFilterSelected,
          })}
          variant="secondary"
          onClick={clearLocalFilters}
          disabled={noLocalFilterSelected}
        >
          Clear
        </ButtonV2>
      }
    >
      <div className={styles.sidebarContent}>
        {hasMagnet && (
          <MagnetMenu
            activeValue={localFilters.has_magnet}
            updateFilter={updateLocalFilter}
            isMobile
          />
        )}
        {(hasNoCustomPhoto || hasCustomPhoto) && (
          <MobileCustomPhotoMenu
            activeFilters={localFilters}
            updateFilter={updateLocalFilter}
            individualVisibilityCheck={{
              has_custom_photo: hasCustomPhoto,
              has_no_custom_photo: hasNoCustomPhoto,
            }}
          />
        )}
        {hasFoil && (
          <CustomFoilMenu
            showModal={showModal}
            activeValue={localFilters.has_foil}
            updateFilter={updateLocalFilter}
            isMobile
          />
        )}
        {hasLetterpress && (
          <LetterpressMenu
            showModal={showModal}
            activeValue={localFilters.is_letterpress}
            updateFilter={updateLocalFilter}
            isMobile
          />
        )}
        {isVowRenewal && (
          <ReceptionMenu
            activeValue={localFilters.is_vow_renewal}
            updateFilter={updateLocalFilter}
            isMobile
          />
        )}
        {hasMultipleOrientations && (
          <MobileOrientationMenu activeFilters={localFilters} updateFilter={updateLocalFilter} />
        )}
        {hasThirdPartyDesigns && (
          <FeaturedArtistMenu activeFilters={localFilters} updateFilter={updateLocalFilter} />
        )}
      </div>
      <div className={styles.filterActions}>
        <ButtonV2 disabled={cardCatalogBusy} className={styles.fullWidth} onClick={applyFilter}>
          {cardCatalogBusy ? 'Please wait...' : 'Apply'}
        </ButtonV2>
      </div>
    </ProvisionalSideDrawer>
  );
};

type MoreFiltersMobileSubset = Omit<ActiveFilters, 'motifs' | 'multiColor'>;
interface MoreFiltersMobileProps
  extends Pick<
    CardSettings,
    | 'hasLetterpress'
    | 'hasFoil'
    | 'hasCustomPhoto'
    | 'hasNoCustomPhoto'
    | 'isVowRenewal'
    | 'hasMultipleOrientations'
    | 'hasThirdPartyDesigns'
  > {
  cardCatalogBusy: boolean;
  activeFilters: MoreFiltersMobileSubset;
  updateMultipleFilters: (filters: Partial<MoreFiltersMobileSubset>) => void;
  onClose: () => void;
  displayMoreFilters: boolean;
  hasMagnet: boolean;
  showModal: (
    modalType: string,
    modalProps: Record<string, unknown>,
    modalOptions: ModalOptions
  ) => void;
}

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  showModal: (modalType: string, modalProps: Record<string, unknown>, modalOptions: ModalOptions) =>
    dispatch(openModal(modalType, modalProps, modalOptions)),
});

export default connect(null, mapDispatchToProps)(MoreFiltersMobile);
